import React, { useEffect, useState } from "react";
import Calling from '../../../api/Calling';
import Loader from '../../../components/Loader';

import { toast } from "react-toastify";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";
import { FiCalendar, FiTrendingUp, FiActivity, FiEye, FiMousePointer, FiSearch, FiFilter, FiAlertCircle, FiCheckCircle, FiXCircle, FiDownload, FiX } from "react-icons/fi";
import MainButton from "../../../components/ul/MainButton";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const EventDetailModal = ({ eventType, eventData, allAdsData, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('count');
    const [sortOrder, setSortOrder] = useState('desc');
    const [expandedTimelines, setExpandedTimelines] = useState({});

    const getEventColor = (eventType) => {
        const colors = {
            onAdClicked: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', gradient: 'from-yellow-500 to-yellow-600' },
            onAdLoaded: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', gradient: 'from-blue-500 to-blue-600' },
            onAdFailedToLoad: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', gradient: 'from-red-500 to-red-600' },
            onAdImpression: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300', gradient: 'from-purple-500 to-purple-600' },
            onAdShowedFullScreenContent: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', gradient: 'from-green-500 to-green-600' },
            onAdDismissedFullScreenContent: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', gradient: 'from-orange-500 to-orange-600' },
            onAdFailedToShowFullScreenContent: { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-300', gradient: 'from-pink-500 to-pink-600' },
            onAdUnitNotFound: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300', gradient: 'from-gray-500 to-gray-600' },
            onRequestSend: { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-300', gradient: 'from-cyan-500 to-cyan-600' },
        };
        return colors[eventType] || { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300', gradient: 'from-gray-500 to-gray-600' };
    };

    const eventColor = getEventColor(eventType);

    const getGroupedEventData = () => {
        const grouped = {};
        const adConfigMap = {};

        Object.values(allAdsData).flat().forEach(ad => {
            if (ad._id) {
                adConfigMap[ad._id] = ad;
            }
        });

        eventData
            .filter(event => event.a_event === eventType)
            .forEach(event => {
                const key = event.record_id;

                if (!grouped[key]) {
                    const adConfig = adConfigMap[event.record_id];

                    if (adConfig) {
                        grouped[key] = {
                            id: key,
                            record_id: event.record_id,
                            a_id: adConfig.a_id,
                            a_name: adConfig.a_name || 'Unknown Ad',
                            a_description: adConfig.a_description || '',
                            totalCount: 0,
                            events: []
                        };
                    } else {
                        grouped[key] = {
                            id: key,
                            record_id: event.record_id,
                            a_id: event.a_id || 'Unknown',
                            a_name: event.a_name || 'Unknown Ad',
                            a_description: event.a_description || '',
                            totalCount: 0,
                            events: []
                        };
                    }
                }

                grouped[key].totalCount += event.a_count || 0;
                grouped[key].events.push(event);
            });

        return Object.values(grouped).filter(item => item.events.length > 0);
    };

    const groupedData = getGroupedEventData();

    const filteredData = groupedData.filter(item => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            item.a_name?.toLowerCase().includes(searchLower) ||
            item.a_id?.toLowerCase().includes(searchLower) ||
            item.a_description?.toLowerCase().includes(searchLower) ||
            item.record_id?.toLowerCase().includes(searchLower)
        );
    });

    const sortedData = [...filteredData].sort((a, b) => {
        let comparison = 0;

        switch (sortBy) {
            case 'count':
                comparison = a.totalCount - b.totalCount;
                break;
            case 'date':
                const aLatest = Math.max(...a.events.map(e => new Date(e.createdAt).getTime()));
                const bLatest = Math.max(...b.events.map(e => new Date(e.createdAt).getTime()));
                comparison = aLatest - bLatest;
                break;
            case 'name':
                comparison = (a.a_name || '').localeCompare(b.a_name || '');
                break;
            default:
                comparison = 0;
        }

        return sortOrder === 'asc' ? comparison : -comparison;
    });

    const totalEvents = sortedData.reduce((sum, item) => sum + item.totalCount, 0);

    const toggleTimeline = (itemId) => {
        setExpandedTimelines(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    };

    const groupEventsByDate = (events) => {
        const grouped = {};

        events.forEach(event => {
            const dateKey = new Date(event.createdAt).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });

            if (!grouped[dateKey]) {
                grouped[dateKey] = {
                    date: dateKey,
                    timestamp: new Date(event.createdAt).getTime(),
                    events: []
                };
            }

            grouped[dateKey].events.push(event);
        });

        return Object.values(grouped).sort((a, b) => b.timestamp - a.timestamp);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className={`flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r ${eventColor.gradient}`}>
                    <div>
                        <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                            <FiActivity className="text-white" />
                            {eventType} Analytics
                        </h3>
                        <p className="text-white text-opacity-90 mt-2">
                            {sortedData.length} configurations • {totalEvents.toLocaleString()} total events
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-10 h-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-colors backdrop-blur-sm"
                    >
                        <FiX className="text-white text-xl" />
                    </button>
                </div>

                <div className="p-6 border-b border-gray-200 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                            <div className="relative">
                                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search ad name, ID, description..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                            >
                                <option value="count">Event Count</option>
                                <option value="date">Latest Event</option>
                                <option value="name">Ad Name</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Sort Order</label>
                            <select
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                            >
                                <option value="desc">Highest First</option>
                                <option value="asc">Lowest First</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="p-6 overflow-auto max-h-[calc(90vh-280px)]">
                    {sortedData.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                            <FiAlertCircle className="text-5xl mb-3" />
                            <p className="text-lg font-medium">No records found</p>
                            <p className="text-sm">Try adjusting your search</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {sortedData.map((item) => {
                                const groupedByDate = groupEventsByDate(item.events);
                                const isExpanded = expandedTimelines[item.id];
                                const visibleDates = isExpanded ? groupedByDate : groupedByDate.slice(0, 1);
                                const hasMoreDates = groupedByDate.length > 1;

                                return (
                                    <div
                                        key={item.id}
                                        className={`${eventColor.bg} border-2 ${eventColor.border} rounded-xl p-5 hover:shadow-lg transition-all`}
                                    >
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1">
                                                <h4 className="font-bold text-gray-800 text-lg mb-1">
                                                    {item.a_name}
                                                </h4>
                                                {item.a_description && (
                                                    <p className="text-sm text-gray-600 mb-2">
                                                        {item.a_description.length > 80
                                                            ? `${item.a_description.substring(0, 80)}...`
                                                            : item.a_description}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="ml-4">
                                                <div className="w-16 h-16 bg-white rounded-lg flex flex-col items-center justify-center shadow-md">
                                                    <span className="text-2xl font-bold text-gray-800">
                                                        {item.totalCount}
                                                    </span>
                                                    <span className="text-xs text-gray-500">events</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mb-3 p-2 bg-white bg-opacity-50 rounded-lg">
                                            <span className="text-xs font-medium text-gray-600">Ad ID:</span>
                                            <p className="text-xs font-mono text-gray-800 truncate mt-1" title={item.a_id}>
                                                {item.a_id}
                                            </p>
                                        </div>

                                        <div className="mb-3 p-2 bg-white bg-opacity-50 rounded-lg">
                                            <span className="text-xs font-medium text-gray-600">Record ID:</span>
                                            <p className="text-xs font-mono text-gray-800 truncate mt-1" title={item.record_id}>
                                                {item.record_id}
                                            </p>
                                        </div>

                                        <div className="border-t border-gray-300 pt-3">
                                            <div className="flex items-center justify-between mb-2">
                                                <h5 className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                                                    <FiCalendar className="text-xs" />
                                                    Event Timeline ({item.events.length} occurrences)
                                                </h5>
                                                {hasMoreDates && (
                                                    <span className="text-xs text-gray-500">
                                                        {groupedByDate.length} dates
                                                    </span>
                                                )}
                                            </div>
                                            <div className="space-y-2">
                                                {visibleDates.map((dateGroup, dateIdx) => {
                                                    const dateTotal = dateGroup.events.reduce((sum, e) => sum + (e.a_count || 0), 0);

                                                    return (
                                                        <div key={dateIdx} className="bg-white bg-opacity-70 rounded-lg p-3">
                                                            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
                                                                <div className="flex items-center gap-2">
                                                                    <FiCalendar className="text-gray-500 text-sm" />
                                                                    <span className="text-sm font-bold text-gray-800">
                                                                        {dateGroup.date}
                                                                    </span>
                                                                </div>
                                                                <span className="text-sm font-bold text-gray-800 bg-white px-2 py-1 rounded">
                                                                    {dateTotal} events
                                                                </span>
                                                            </div>

                                                            <div className="space-y-1.5 max-h-24 overflow-y-auto">
                                                                {dateGroup.events
                                                                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                                                                    .map((event, eventIdx) => (
                                                                        <div
                                                                            key={eventIdx}
                                                                            className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded"
                                                                        >
                                                                            <span className="text-gray-600">
                                                                                {new Date(event.createdAt).toLocaleTimeString('en-US', {
                                                                                    hour: '2-digit',
                                                                                    minute: '2-digit',
                                                                                    second: '2-digit'
                                                                                })}
                                                                            </span>
                                                                            <span className="font-bold text-gray-800">
                                                                                {event.a_count}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })}

                                                {hasMoreDates && (
                                                    <button
                                                        onClick={() => toggleTimeline(item.id)}
                                                        className="w-full mt-2 px-3 py-2 bg-white hover:bg-gray-50 border-2 border-gray-300 rounded-lg text-xs font-semibold text-gray-700 transition-all flex items-center justify-center gap-2"
                                                    >
                                                        {isExpanded ? (
                                                            <>
                                                                <FiActivity className="rotate-180" />
                                                                Show Less ({groupedByDate.length - 1} hidden)
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FiActivity />
                                                                Show {groupedByDate.length - 1} More Date{groupedByDate.length - 1 !== 1 ? 's' : ''}
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const DataTypeFilter = ({ selectedType, onTypeChange }) => {
    const types = [
        { value: 'all', label: 'All' },
        { value: 'bannerAds', label: 'Banner' },
        { value: 'fullScreenAds', label: 'Full Screen' },
        { value: 'nativeAds', label: 'Native' },
        { value: 'openAds', label: 'Open' },
        { value: 'rewardAds', label: 'Reward' },
    ];

    return (
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-700 mr-2">Filter by Type:</span>
                {types.map((type) => (
                    <button
                        key={type.value}
                        onClick={() => onTypeChange(type.value)}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${selectedType === type.value
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-600 border border-gray-300 hover:border-blue-400'
                            }`}
                    >
                        {type.label}
                    </button>
                ))}
            </div>
        </div>
    );
};

const EventsDetailModal = ({ eventsByDate, adName, onClose }) => {
    const [selectedEventFilter, setSelectedEventFilter] = useState('all');

    const allEventTypes = new Set();
    Object.values(eventsByDate).forEach(events => {
        events.forEach(event => allEventTypes.add(event.a_event));
    });

    const getFilteredEvents = () => {
        if (selectedEventFilter === 'all') return eventsByDate;

        const filtered = {};
        Object.keys(eventsByDate).forEach(date => {
            const filteredDateEvents = eventsByDate[date].filter(
                event => event.a_event === selectedEventFilter
            );
            if (filteredDateEvents.length > 0) {
                filtered[date] = filteredDateEvents;
            }
        });
        return filtered;
    };

    const filteredEvents = getFilteredEvents();

    const eventTypeCounts = {};
    Object.values(eventsByDate).forEach(events => {
        events.forEach(event => {
            if (!eventTypeCounts[event.a_event]) {
                eventTypeCounts[event.a_event] = 0;
            }
            eventTypeCounts[event.a_event] += event.a_count || 0;
        });
    });

    const getEventColor = (eventType) => {
        const colors = {
            onAdClicked: 'bg-yellow-100 text-yellow-700 border-yellow-300',
            onAdLoaded: 'bg-blue-100 text-blue-700 border-blue-300',
            onAdFailedToLoad: 'bg-red-100 text-red-700 border-red-300',
            onAdImpression: 'bg-purple-100 text-purple-700 border-purple-300',
            onAdShowedFullScreenContent: 'bg-green-100 text-green-700 border-green-300',
            onAdDismissedFullScreenContent: 'bg-orange-100 text-orange-700 border-orange-300',
            onAdFailedToShowFullScreenContent: 'bg-pink-100 text-pink-700 border-pink-300',
            onAdUnitNotFound: 'bg-gray-100 text-gray-700 border-gray-300',
            onRequestSend: 'bg-cyan-100 text-cyan-700 border-cyan-300',
        };
        return colors[eventType] || 'bg-gray-100 text-gray-700 border-gray-300';
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div>
                        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                            <FiActivity className="text-blue-600" />
                            Event Details: {adName}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                            {Object.keys(eventsByDate).length} dates • {Object.values(eventsByDate).flat().length} total events
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-10 h-10 bg-red-100 hover:bg-red-200 rounded-full flex items-center justify-center transition-colors"
                    >
                        <FiX className="text-red-600 text-xl" />
                    </button>
                </div>

                <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center gap-2 mb-3">
                        <FiFilter className="text-gray-600" />
                        <span className="text-sm font-semibold text-gray-700">Filter by Event Type:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => setSelectedEventFilter('all')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${selectedEventFilter === 'all'
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-400'
                                }`}
                        >
                            All Events ({Object.values(eventsByDate).flat().length})
                        </button>
                        {Array.from(allEventTypes).sort().map(eventType => (
                            <button
                                key={eventType}
                                onClick={() => setSelectedEventFilter(eventType)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${selectedEventFilter === eventType
                                    ? getEventColor(eventType) + ' shadow-md border'
                                    : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400'
                                    }`}
                            >
                                {eventType} ({eventTypeCounts[eventType]})
                            </button>
                        ))}
                    </div>
                </div>

                <div className="p-6 overflow-auto max-h-[calc(90vh-250px)]">
                    {Object.keys(filteredEvents).length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                            <FiAlertCircle className="text-5xl mb-3" />
                            <p className="text-lg font-medium">No events found</p>
                            <p className="text-sm">Try selecting a different event type</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {Object.keys(filteredEvents)
                                .sort((a, b) => {
                                    const dateA = a.split('-').reverse().join('-');
                                    const dateB = b.split('-').reverse().join('-');
                                    return new Date(dateB) - new Date(dateA);
                                })
                                .map((date, dateIdx) => (
                                    <div key={dateIdx} className="bg-white border-2 border-gray-200 rounded-xl p-4 hover:shadow-lg transition-shadow">
                                        <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-200">
                                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                                <FiCalendar className="text-blue-600 text-xl" />
                                            </div>
                                            <div>
                                                <h4 className="text-lg font-bold text-gray-800">{date}</h4>
                                                <p className="text-sm text-gray-500">
                                                    {filteredEvents[date].length} event{filteredEvents[date].length !== 1 ? 's' : ''} •{' '}
                                                    Total count: {filteredEvents[date].reduce((sum, e) => sum + (e.a_count || 0), 0)}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {filteredEvents[date].map((event, idx) => (
                                                <div
                                                    key={idx}
                                                    className={`${getEventColor(event.a_event)} border-2 rounded-lg p-4 hover:shadow-md transition-all`}
                                                >
                                                    <div className="flex items-start justify-between mb-2">
                                                        <span className="text-xs font-bold uppercase tracking-wide">
                                                            {event.a_event}
                                                        </span>
                                                        <span className="text-lg font-bold">
                                                            {event.a_count}
                                                        </span>
                                                    </div>
                                                    <div className="text-xs opacity-75 space-y-1">
                                                        <div className="flex items-center gap-1">
                                                            <FiCalendar className="text-xs" />
                                                            <span>
                                                                {new Date(event.createdAt).toLocaleTimeString('en-US', {
                                                                    hour: '2-digit',
                                                                    minute: '2-digit',
                                                                    second: '2-digit'
                                                                })}
                                                            </span>
                                                        </div>
                                                        {event.a_id && (
                                                            <div className="text-xs font-mono truncate" title={event.a_id}>
                                                                ID: {event.a_id.substring(0, 20)}...
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const AdsTrack = () => {
    const [adsData, setAdsData] = useState([]);
    const [allAdsData, setAllAdsData] = useState({});
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(false);
    const [selectedBtn, setSelectedBtn] = useState('today');
    const [selectedType, setSelectedType] = useState('all');
    const [selectedAdId, setSelectedAdId] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [showEventsModal, setShowEventsModal] = useState(false);
    const [selectedEventsByDate, setSelectedEventsByDate] = useState({});
    const [selectedAdName, setSelectedAdName] = useState('');

    const [showEventDetailModal, setShowEventDetailModal] = useState(false);
    const [selectedEventType, setSelectedEventType] = useState('');

    const [summary, setSummary] = useState({
        bannerAds: { total: 0, totalCount: 0 },
        fullScreenAds: { total: 0, totalCount: 0 },
        nativeAds: { total: 0, totalCount: 0 },
        openAds: { total: 0, totalCount: 0 },
        rewardAds: { total: 0, totalCount: 0 },
    });

    const fetchAdsData = async () => {
        setLoading(true);
        try {
            const response = await Calling.adminAdsTrackDashbord(startDate, endDate, selectedType);
            if (response?.status) {
                setSummary(response?.summary || {});
                setAdsData(response?.adsEvents || []);
                setAllAdsData({
                    bannerAds: response?.bannerAds || [],
                    fullScreenAds: response?.fullScreenAds || [],
                    nativeAds: response?.nativeAds || [],
                    openAds: response?.openAds || [],
                    rewardAds: response?.rewardAds || [],
                });
            } else {
                toast.error(response?.message || "Failed to fetch Ads data");
            }
        } catch (error) {
            console.error("Error fetching Ads data:", error);
            toast.error("Server error while fetching Ads data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdsData();
    }, [startDate, endDate, selectedBtn, selectedType]);

    const handleDateRangeFilter = (range) => {
        const todayUTC = new Date();
        let start, end;

        // Helper to convert a UTC date to IST and format YYYY-MM-DD
        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in milliseconds
            const istDate = new Date(date.getTime() + istOffset);
            return istDate.toISOString().split('T')[0];
        };

        switch (range) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                setSelectedBtn('today');
                break;

            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                setSelectedBtn('yesterday');
                break;

            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);
                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                setSelectedBtn('thisWeek');
                break;

            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                setSelectedBtn('thisMonth');
                break;

            case 'last6Months':
                const firstDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() - 5, 1);
                const lastDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayLast6UTC);
                end = toIST(lastDayLast6UTC);
                setSelectedBtn('last6Months');
                break;

            case 'thisYear':
                const firstDayOfYearUTC = new Date(todayUTC.getFullYear(), 0, 1);
                const lastDayOfYearUTC = new Date(todayUTC.getFullYear(), 11, 31);
                start = toIST(firstDayOfYearUTC);
                end = toIST(lastDayOfYearUTC);
                setSelectedBtn('thisYear');
                break;

            case 'lastYear':
                const firstDayLastYearUTC = new Date(todayUTC.getFullYear() - 1, 0, 1);
                const lastDayLastYearUTC = new Date(todayUTC.getFullYear() - 1, 11, 31);
                start = toIST(firstDayLastYearUTC);
                end = toIST(lastDayLastYearUTC);
                setSelectedBtn('lastYear');
                break;

            default:
                start = '';
                end = '';
                setSelectedBtn('range');
        }

        setStartDate(start);
        setEndDate(end);
    };

    const handleFormDate = (e) => {
        const { name, value } = e.target;
        if (name === 'startDate') {
            setStartDate(value);
        } else {
            setEndDate(value);
        }
        setSelectedBtn('range');
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        fetchAdsData();
    };

    const handleDeleteData = async () => {
        if (!startDate || !endDate) {
            toast.error("Please select a valid date range");
            return;
        }

        setDeleteLoading(true);
        try {
            const response = await Calling.adminAdsTrackDelete(
                startDate,
                endDate,
                selectedType
            );

            if (response?.status) {
                toast.success(response?.message || "Data deleted successfully");
                fetchAdsData();
                setShowDeleteModal(false);
            } else {
                toast.error(response?.message || "Failed to delete data");
            }
        } catch (error) {
            console.error("Error deleting data:", error);
            toast.error("Server error while deleting data");
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleViewEventDetails = (eventsByDate, adName) => {
        setSelectedEventsByDate(eventsByDate);
        setSelectedAdName(adName);
        setShowEventsModal(true);
    };

    const handleExportCSV = () => {
        try {
            const exportData = prepareExportData();

            if (exportData.length === 0) {
                toast.warning("No data available to export");
                return;
            }

            const headers = Object.keys(exportData[0]).join(',');
            const rows = exportData.map(row =>
                Object.values(row).map(value =>
                    typeof value === 'string' && (value.includes(',') || value.includes('"'))
                        ? `"${value.replace(/"/g, '""')}"`
                        : value
                ).join(',')
            );

            const csv = [headers, ...rows].join('\n');

            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.setAttribute('href', url);
            link.setAttribute('download', `AdsTrack_${startDate}_to_${endDate}.csv`);
            link.style.visibility = 'hidden';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success("CSV file downloaded successfully");
        } catch (error) {
            console.error("Error exporting CSV:", error);
            toast.error("Failed to export CSV");
        }
    };

    const prepareExportData = () => {
        const tableData = getGroupedTableData();
        const exportData = [];

        tableData.forEach((item, index) => {
            if (item.eventsByDate && Object.keys(item.eventsByDate).length > 0) {
                Object.keys(item.eventsByDate).forEach((date) => {
                    const eventCounts = {
                        onAdLoaded: 0,
                        onAdFailedToLoad: 0,
                        onAdShowedFullScreenContent: 0,
                        onAdDismissedFullScreenContent: 0,
                        onAdFailedToShowFullScreenContent: 0,
                        onAdImpression: 0,
                        onAdClicked: 0,
                        onAdUnitNotFound: 0,
                        onRequestSend: 0
                    };

                    item.eventsByDate[date].forEach((event) => {
                        const eventType = event.a_event;
                        if (eventCounts.hasOwnProperty(eventType)) {
                            eventCounts[eventType] += event.a_count || 0;
                        }
                    });

                    exportData.push({
                        '#': index + 1,
                        'Ad Name': item.a_name || 'N/A',
                        'Ad ID': item.a_id || 'N/A',
                        'Description': item.a_description || 'N/A',
                        'Event Date': date,
                        'onAdLoaded': eventCounts.onAdLoaded,
                        'onAdFailedToLoad': eventCounts.onAdFailedToLoad,
                        'onAdShowedFullScreenContent': eventCounts.onAdShowedFullScreenContent,
                        'onAdDismissedFullScreenContent': eventCounts.onAdDismissedFullScreenContent,
                        'onAdFailedToShowFullScreenContent': eventCounts.onAdFailedToShowFullScreenContent,
                        'onAdImpression': eventCounts.onAdImpression,
                        'onAdClicked': eventCounts.onAdClicked,
                        'onAdUnitNotFound': eventCounts.onAdUnitNotFound,
                        'onRequestSend': eventCounts.onRequestSend
                    });
                });
            }
        });

        return exportData;
    };

    const DeleteModal = () => {
        const getAffectedRecordsCount = () => {
            let allData = [];
            if (selectedType === 'all') {
                allData = Object.values(allAdsData).flat();
            } else {
                allData = allAdsData[selectedType] || [];
            }
            return allData.length;
        };

        const affectedRecords = getAffectedRecordsCount();

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                    <div className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                <FiAlertCircle className="text-red-600 text-2xl" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800">Delete Ad Track Data</h3>
                        </div>

                        <div className="mb-6 space-y-3">
                            <p className="text-gray-600">
                                You are about to delete ad tracking data with the following filters:
                            </p>

                            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-sm font-medium text-gray-600">Date Range:</span>
                                    <span className="text-sm font-semibold text-gray-800">
                                        {startDate} to {endDate}
                                    </span>
                                </div>
                                <div className="flex justify-between border-t pt-2">
                                    <span className="text-sm font-medium text-gray-600">Ad Type:</span>
                                    <span className="text-sm font-semibold text-gray-800">
                                        {selectedType === 'all' ? 'All Types' : selectedType}
                                    </span>
                                </div>
                                <div className="flex justify-between border-t pt-2">
                                    <span className="text-sm font-medium text-gray-600">Estimated Records:</span>
                                    <span className="text-sm font-bold text-red-600">
                                        ~{affectedRecords} configurations
                                    </span>
                                </div>
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <p className="text-sm text-blue-700 font-medium flex items-start gap-2">
                                    <FiAlertCircle className="mt-0.5 flex-shrink-0" />
                                    <span>Ad ID filters will not affect deletion. Only Date Range and Type will be used.</span>
                                </p>
                            </div>

                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <p className="text-sm text-red-700 font-medium">
                                    ⚠️ This action cannot be undone. All data matching the date range and type will be permanently deleted.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                disabled={deleteLoading}
                                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteData}
                                disabled={deleteLoading}
                                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {deleteLoading ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <FiXCircle />
                                        Delete Data
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const eventColors = {
        onRequestSend: { bg: 'rgba(59, 130, 246, 0.3)', border: 'rgba(59, 130, 246, 1)' },
        onAdLoaded: { bg: 'rgba(59, 130, 246, 0.3)', border: 'rgba(59, 130, 246, 1)' },
        onAdFailedToLoad: { bg: 'rgba(239, 68, 68, 0.3)', border: 'rgba(239, 68, 68, 1)' },
        onAdShowedFullScreenContent: { bg: 'rgba(16, 185, 129, 0.3)', border: 'rgba(16, 185, 129, 1)' },
        onAdDismissedFullScreenContent: { bg: 'rgba(251, 146, 60, 0.3)', border: 'rgba(251, 146, 60, 1)' },
        onAdFailedToShowFullScreenContent: { bg: 'rgba(236, 72, 153, 0.3)', border: 'rgba(236, 72, 153, 1)' },
        onAdImpression: { bg: 'rgba(139, 92, 246, 0.3)', border: 'rgba(139, 92, 246, 1)' },
        onAdClicked: { bg: 'rgba(234, 179, 8, 0.3)', border: 'rgba(234, 179, 8, 1)' },
        onAdUnitNotFound: { bg: 'rgba(107, 114, 128, 0.3)', border: 'rgba(107, 114, 128, 1)' },
    };

    const filteredAdsData = adsData;

    const getFilteredAdsData = () => {
        let filtered = [...adsData];

        if (selectedAdId !== 'all') {
            filtered = filtered.filter(event => event.a_id === selectedAdId);
        }

        return filtered;
    };

    const uiFilteredAdsData = getFilteredAdsData();

    const eventsByDate = {};
    uiFilteredAdsData.forEach((event) => {
        const date = new Date(event.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
        if (!eventsByDate[date]) {
            eventsByDate[date] = {};
        }
        if (!eventsByDate[date][event.a_event]) {
            eventsByDate[date][event.a_event] = 0;
        }
        eventsByDate[date][event.a_event] += event.a_count || 0;
    });

    const chartLabels = Object.keys(eventsByDate).sort((a, b) => {
        return new Date(a) - new Date(b);
    });

    const allEventTypes = [...new Set(uiFilteredAdsData.map(e => e.a_event))];

    const chartDatasets = allEventTypes.map((eventType) => {
        const colors = eventColors[eventType] || { bg: 'rgba(156, 163, 175, 0.3)', border: 'rgba(156, 163, 175, 1)' };

        return {
            label: eventType,
            data: chartLabels.map(date => eventsByDate[date][eventType] || 0),
            fill: true,
            backgroundColor: colors.bg,
            borderColor: colors.border,
            borderWidth: 2,
            tension: 0.4,
            pointRadius: 4,
            pointBackgroundColor: colors.border,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointHoverRadius: 6,
        };
    });

    const chartData = {
        labels: chartLabels,
        datasets: chartDatasets,
    };

    const getEventAnalytics = () => {
        const analytics = {
            totalEvents: uiFilteredAdsData.length,
            totalCount: uiFilteredAdsData.reduce((sum, event) => sum + (event.a_count || 0), 0),
            onAdLoaded: 0,
            onAdFailedToLoad: 0,
            onAdShowedFullScreenContent: 0,
            onAdDismissedFullScreenContent: 0,
            onAdFailedToShowFullScreenContent: 0,
            onAdImpression: 0,
            onAdClicked: 0,
            onAdUnitNotFound: 0,
            onRequestSend: 0,
        };

        uiFilteredAdsData.forEach(event => {
            const count = event.a_count || 0;
            const eventType = event.a_event;
            if (analytics[eventType] !== undefined) {
                analytics[eventType] += count;
            }
        });

        return analytics;
    };

    const eventAnalytics = getEventAnalytics();

    const summaryCards = [
        {
            key: 'bannerAds',
            label: 'Banner Ads',
            color: 'from-blue-500 to-blue-600',
            bgColor: 'bg-blue-50',
            textColor: 'text-blue-600'
        },
        {
            key: 'fullScreenAds',
            label: 'Full Screen',
            color: 'from-purple-500 to-purple-600',
            bgColor: 'bg-purple-50',
            textColor: 'text-purple-600'
        },
        {
            key: 'nativeAds',
            label: 'Native Ads',
            color: 'from-green-500 to-green-600',
            bgColor: 'bg-green-50',
            textColor: 'text-green-600'
        },
        {
            key: 'openAds',
            label: 'Open Ads',
            color: 'from-orange-500 to-orange-600',
            bgColor: 'bg-orange-50',
            textColor: 'text-orange-600'
        },
        {
            key: 'rewardAds',
            label: 'Reward Ads',
            color: 'from-pink-500 to-pink-600',
            bgColor: 'bg-pink-50',
            textColor: 'text-pink-600'
        },
    ];

    const eventCards = [
        {
            label: 'Request Send',
            value: eventAnalytics.onRequestSend,
            subValue: 'Ad Requests',
            icon: FiTrendingUp,
            color: 'from-cyan-500 to-cyan-600',
            bgColor: 'bg-cyan-50',
            textColor: 'text-cyan-600',
            eventType: 'onRequestSend'
        },
        {
            label: 'Ad Loaded',
            value: eventAnalytics.onAdLoaded,
            subValue: 'Success',
            icon: FiCheckCircle,
            color: 'from-blue-500 to-blue-600',
            bgColor: 'bg-blue-50',
            textColor: 'text-blue-600',
            eventType: 'onAdLoaded'
        },
        {
            label: 'Load Failed',
            value: eventAnalytics.onAdFailedToLoad,
            subValue: 'Load Errors',
            icon: FiXCircle,
            color: 'from-red-500 to-red-600',
            bgColor: 'bg-red-50',
            textColor: 'text-red-600',
            eventType: 'onAdFailedToLoad'
        },
        {
            label: 'Showed Full Screen',
            value: eventAnalytics.onAdShowedFullScreenContent,
            subValue: 'Displayed',
            icon: FiEye,
            color: 'from-green-500 to-green-600',
            bgColor: 'bg-green-50',
            textColor: 'text-green-600',
            eventType: 'onAdShowedFullScreenContent'
        },
        {
            label: 'Dismissed',
            value: eventAnalytics.onAdDismissedFullScreenContent,
            subValue: 'Closed',
            icon: FiActivity,
            color: 'from-orange-500 to-orange-600',
            bgColor: 'bg-orange-50',
            textColor: 'text-orange-600',
            eventType: 'onAdDismissedFullScreenContent'
        },
        {
            label: 'Show Failed',
            value: eventAnalytics.onAdFailedToShowFullScreenContent,
            subValue: 'Display Errors',
            icon: FiAlertCircle,
            color: 'from-pink-500 to-pink-600',
            bgColor: 'bg-pink-50',
            textColor: 'text-pink-600',
            eventType: 'onAdFailedToShowFullScreenContent'
        },
        {
            label: 'Impressions',
            value: eventAnalytics.onAdImpression,
            subValue: 'Views',
            icon: FiEye,
            color: 'from-purple-500 to-purple-600',
            bgColor: 'bg-purple-50',
            textColor: 'text-purple-600',
            eventType: 'onAdImpression'
        },
        {
            label: 'Clicks',
            value: eventAnalytics.onAdClicked,
            subValue: 'Interactions',
            icon: FiMousePointer,
            color: 'from-yellow-500 to-yellow-600',
            bgColor: 'bg-yellow-50',
            textColor: 'text-yellow-600',
            eventType: 'onAdClicked'
        },
        {
            label: 'Unit Not Found',
            value: eventAnalytics.onAdUnitNotFound,
            subValue: 'Configuration Errors',
            icon: FiAlertCircle,
            color: 'from-gray-500 to-gray-600',
            bgColor: 'bg-gray-50',
            textColor: 'text-gray-600',
            eventType: 'onAdUnitNotFound'
        },
    ];

    const getUniqueAdIds = () => {
        const allData = Object.values(allAdsData).flat();
        const uniqueIds = [...new Set(allData.map(item => item.a_id).filter(id => id))];
        return uniqueIds;
    };

    const getGroupedTableData = () => {
        let filteredData = [];

        if (selectedType === 'all') {
            filteredData = Object.values(allAdsData).flat();
        } else {
            filteredData = allAdsData[selectedType] || [];
        }

        if (selectedAdId !== 'all') {
            filteredData = filteredData.filter(item => item.a_id === selectedAdId);
        }

        if (searchTerm) {
            filteredData = filteredData.filter(item =>
                item.a_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.a_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.a_description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        const grouped = {};
        filteredData.forEach(item => {
            const key = item._id;
            if (!grouped[key]) {
                grouped[key] = {
                    ...item,
                    eventsByDate: {}
                };
            }
        });

        uiFilteredAdsData.forEach(event => {
            const matchingKey = Object.keys(grouped).find(key => grouped[key]._id === event.record_id);

            if (matchingKey) {
                const eventDate = new Date(event.createdAt).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });

                if (!grouped[matchingKey].eventsByDate[eventDate]) {
                    grouped[matchingKey].eventsByDate[eventDate] = [];
                }

                grouped[matchingKey].eventsByDate[eventDate].push(event);
            }
        });

        return Object.values(grouped);
    };

    const handleEventCardClick = (eventType) => {
        setSelectedEventType(eventType);
        setShowEventDetailModal(true);
    };

    const tableData = getGroupedTableData();
    const uniqueAdIds = getUniqueAdIds();

    return (
        <div className='w-full min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 text-main-font pt-20 pb-8 px-4 md:px-6 md:ml-[4.4rem]'>
            <div className="mb-4 mt-2">
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                        <FiActivity className="text-white text-xl" />
                    </div>
                    <h1 className="text-3xl font-bold text-gray-800">
                        Ads Analytics Dashboard
                    </h1>
                </div>
                <p className="text-gray-600 ml-[52px]">Track and analyze your ad performance in real-time</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-6">
                <div className="p-6">
                    <div className="flex items-center gap-2 mb-5">
                        <FiCalendar className="text-blue-600 text-lg" />
                        <h2 className="text-lg font-semibold text-gray-800">Date Range Selection</h2>
                    </div>
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12 lg:col-span-8 flex flex-wrap gap-3">
                            <MainButton
                                onClick={() => handleDateRangeFilter('today')}
                                label={'Today'}
                                selectState={selectedBtn === "today"}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('yesterday')}
                                label={'Yesterday'}
                                selectState={selectedBtn === "yesterday"}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('thisWeek')}
                                label={'This Week'}
                                selectState={selectedBtn === "thisWeek"}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('thisMonth')}
                                label={'This Month'}
                                selectState={selectedBtn === "thisMonth"}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('last6Months')}
                                label={'Last 6 Months'}
                                selectState={selectedBtn === "last6Months"}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('thisYear')}
                                label={'This Year'}
                                selectState={selectedBtn === "thisYear"}
                            />
                        </div>

                        <div className="col-span-12 lg:col-span-4 flex justify-end">
                            <form onSubmit={handleFilterSubmit} className="w-full lg:w-auto">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="relative">
                                        <input
                                            type="date"
                                            name="startDate"
                                            value={startDate}
                                            onChange={handleFormDate}
                                            min="2023-01-01"
                                            max={new Date().toISOString().split('T')[0]}
                                            className={`w-full px-4 py-2 rounded-lg shadow-sm border cursor-pointer bg-white transition-all ${selectedBtn === "range"
                                                ? 'border-blue-500 ring-2 ring-blue-100 text-blue-600'
                                                : 'border-gray-300 text-gray-700 hover:border-blue-300'
                                                }`}
                                        />
                                    </div>
                                    <div className="relative">
                                        <input
                                            type="date"
                                            name="endDate"
                                            value={endDate}
                                            onChange={handleFormDate}
                                            min="2023-01-01"
                                            max={new Date().toISOString().split('T')[0]}
                                            className={`w-full px-4 py-2 rounded-lg shadow-sm border cursor-pointer bg-white transition-all ${selectedBtn === "range"
                                                ? 'border-blue-500 ring-2 ring-blue-100 text-blue-600'
                                                : 'border-gray-300 text-gray-700 hover:border-blue-300'
                                                }`}
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
                <DataTypeFilter selectedType={selectedType} onTypeChange={setSelectedType} />
            </div>

            {loading && <Loader loader={loading} />}

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
                <div className="flex flex-wrap items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <FiFilter className="text-blue-600 text-lg" />
                        <h3 className="text-lg font-semibold text-gray-800">Advanced Filters</h3>
                    </div>

                    <div className="flex gap-3 my-2 w-full md:w-auto justify-between md:justify-normal md:my-0">
                        <button
                            onClick={fetchAdsData}
                            disabled={loading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <FiActivity className={loading ? 'animate-spin' : ''} />
                            Refresh
                        </button>

                        <div className="relative group">
                            <button className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center gap-2 text-sm shadow-lg hover:shadow-xl">
                                <FiDownload />
                                Export Data
                            </button>
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                                <button
                                    onClick={handleExportCSV}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg flex items-center gap-2"
                                >
                                    <FiDownload className="text-green-600" />
                                    Export as CSV
                                </button>
                            </div>
                        </div>

                        <button
                            onClick={() => setShowDeleteModal(true)}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-2 text-sm shadow-lg hover:shadow-xl"
                        >
                            <FiXCircle />
                            Delete Data
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Filter by Ad ID
                        </label>
                        <select
                            value={selectedAdId}
                            onChange={(e) => setSelectedAdId(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white cursor-pointer"
                        >
                            <option value="all">All Ad IDs</option>
                            {uniqueAdIds.map(id => {
                                const adData = Object.values(allAdsData)
                                    .flat()
                                    .find(item => item.a_id === id);
                                return (
                                    <option key={id} value={id}>
                                        {adData?.a_name || id.substring(0, 40)}
                                        {adData?.a_description ? ` - ${adData.a_description.substring(0, 30)}...` : ''}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Search
                        </label>
                        <div className="relative">
                            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search ad name, ID or description..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                        </div>
                    </div>

                    <div></div>
                    <div></div>
                </div>

                {(selectedAdId !== 'all' || searchTerm) && (
                    <div className="mt-4">
                        <button
                            onClick={() => {
                                setSelectedAdId('all');
                                setSearchTerm('');
                            }}
                            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                        >
                            <FiXCircle /> Clear all filters
                        </button>
                    </div>
                )}
            </div>

            <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FiActivity className="text-blue-600" />
                    Ad Configuration Summary
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                    {summaryCards.map((card) => {
                        const cardData = summary[card.key] || { total: 0, totalCount: 0 };
                        return (
                            <div
                                key={card.key}
                                className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                            >
                                <div className={`h-2 bg-gradient-to-r ${card.color}`}></div>
                                <div className="px-5 py-2">
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                            {card.label} ({cardData.total.toLocaleString()})
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {showDeleteModal && <DeleteModal />}

            {showEventsModal && (
                <EventsDetailModal
                    eventsByDate={selectedEventsByDate}
                    adName={selectedAdName}
                    onClose={() => {
                        setShowEventsModal(false);
                        setSelectedEventsByDate({});
                        setSelectedAdName('');
                    }}
                />
            )}

            {showEventDetailModal && (
                <EventDetailModal
                    eventType={selectedEventType}
                    eventData={uiFilteredAdsData}
                    allAdsData={allAdsData}
                    onClose={() => {
                        setShowEventDetailModal(false);
                        setSelectedEventType('');
                    }}
                />
            )}

            <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FiTrendingUp className="text-blue-600" />
                    Event Analytics (Filtered Data)
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    {eventCards.map((card, index) => {
                        const Icon = card.icon;
                        return (
                            <div
                                key={index}
                                onClick={() => handleEventCardClick(card.eventType)}
                                className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
                            >
                                <div className={`h-2 bg-gradient-to-r ${card.color}`}></div>
                                <div className="p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                            {card.label}
                                        </p>
                                        <div className={`w-10 h-10 ${card.bgColor} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
                                            <Icon className={`${card.textColor} text-lg`} />
                                        </div>
                                    </div>
                                    <div className="mb-1">
                                        <p className="text-2xl font-bold text-gray-800">
                                            {card.value.toLocaleString()}
                                        </p>
                                    </div>
                                    <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
                                        <span className="text-xs text-gray-500">{card.subValue}</span>
                                        <FiEye className="text-gray-400 group-hover:text-blue-600 transition-colors" />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">Ad Events Timeline</h2>
                        <p className="text-sm text-gray-500 mt-1">Track event trends over time</p>
                    </div>
                    <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                        {filteredAdsData.length} events
                    </span>
                </div>
                {filteredAdsData.length > 0 ? (
                    <div className="h-96">
                        <Line
                            data={chartData}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                interaction: {
                                    mode: 'index',
                                    intersect: false,
                                },
                                plugins: {
                                    legend: {
                                        position: "top",
                                        labels: {
                                            usePointStyle: true,
                                            padding: 15,
                                            font: {
                                                size: 11,
                                                weight: '500'
                                            }
                                        }
                                    },
                                    tooltip: {
                                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                        padding: 12,
                                        borderRadius: 8,
                                        titleFont: {
                                            size: 14,
                                            weight: 'bold'
                                        },
                                        bodyFont: {
                                            size: 13
                                        },
                                        callbacks: {
                                            label: function (context) {
                                                return `${context.dataset.label}: ${context.parsed.y} events`;
                                            }
                                        }
                                    }
                                },
                                scales: {
                                    x: {
                                        grid: {
                                            display: false
                                        },
                                        ticks: {
                                            font: {
                                                size: 11
                                            }
                                        }
                                    },
                                    y: {
                                        beginAtZero: true,
                                        grid: {
                                            color: 'rgba(0, 0, 0, 0.05)'
                                        },
                                        ticks: {
                                            font: {
                                                size: 11
                                            },
                                            precision: 0
                                        }
                                    }
                                }
                            }}
                        />
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                        <FiActivity className="text-6xl mb-4" />
                        <p className="text-lg font-medium">No ad events available</p>
                        <p className="text-sm">Try selecting a different date range or adjusting filters</p>
                    </div>
                )}
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">
                                Ad Events by Configuration
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">
                                {tableData.length} configurations found
                            </p>
                        </div>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    #
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Ad Details
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Ad ID
                                </th>
                                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Events Analytics
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {tableData.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-16 text-center">
                                        <FiActivity className="text-5xl text-gray-300 mx-auto mb-3" />
                                        <p className="text-gray-500 font-medium">No records found</p>
                                        <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
                                    </td>
                                </tr>
                            ) : (
                                tableData.map((item, i) => (
                                    <tr
                                        key={item._id}
                                        className="hover:bg-blue-50 transition-colors"
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {i + 1}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium text-gray-900">
                                                    {item.a_name}
                                                </span>
                                                {item.a_description && (
                                                    <span className="text-xs text-gray-500 mt-1">
                                                        {item.a_description.length > 50
                                                            ? `${item.a_description.substring(0, 50)}...`
                                                            : item.a_description}
                                                    </span>
                                                )}
                                                <span className="text-xs text-gray-400 mt-1">
                                                    Created: {new Date(item.createdAt).toLocaleDateString('en-GB', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric'
                                                    })}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded block max-w-xs truncate" title={item.a_id}>
                                                {item.a_id || 'N/A'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            {item.eventsByDate && Object.keys(item.eventsByDate).length > 0 ? (
                                                <button
                                                    onClick={() => handleViewEventDetails(item.eventsByDate, item.a_name)}
                                                    className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg transition-all shadow-md hover:shadow-lg group"
                                                    title="View analytics"
                                                >
                                                    <FiEye className="text-white text-lg group-hover:scale-110 transition-transform" />
                                                </button>
                                            ) : (
                                                <span className="text-xs text-gray-400 italic">No events</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdsTrack;